/**
 * Browser-compatible HTTP client using axios
 * Provides retry logic, timeout handling, and Netlify timeout detection
 */

import axios from 'axios';
import axiosRetry from 'axios-retry';
import {
    HTTP_REQUEST_TIMEOUT_MS,
    HTTP_RETRY_LIMIT,
    HTTP_RETRY_DELAYS_MS,
    HTTP_RETRY_MAX_DELAY_MS,
} from '../utils/constants';
import { addBreadcrumb } from '../utils/sentry-logger';
import { getBaseUrl } from './utils';
import { handleBaseErrors } from './middleware';

/**
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 * @typedef {import('axios').AxiosError} AxiosError
 * @typedef {import('axios').AxiosRequestConfig} AxiosRequestConfig
 * @typedef {import('axios').AxiosInstance} AxiosInstance
 */

/**
 * @typedef {object} HttpClientError
 * @property {string} message - Error message
 * @property {string} name - Error name (e.g., 'HTTPError', 'AxiosError')
 * @property {string} [code] - Error code (e.g., 'NETLIFY_TIMEOUT', 'ECONNABORTED', 'ERR_BAD_REQUEST')
 * @property {boolean} [isNetlifyTimeout] - True if this is a Netlify timeout error
 * @property {AxiosResponse} [response] - The response object (contains status, data, headers)
 * @property {AxiosRequestConfig} [config] - The request configuration
 * @property {XMLHttpRequest | object} [request] - The request object (XMLHttpRequest in browsers, ClientRequest in Node.js)
 */

/**
 * @typedef {AxiosInstance & {extend: (config: object) => HttpClient}} HttpClient
 * The httpClient is an extended AxiosInstance with all standard axios methods:
 * - get(url, config?): Promise<AxiosResponse>
 * - post(url, data?, config?): Promise<AxiosResponse>
 * - put(url, data?, config?): Promise<AxiosResponse>
 * - patch(url, data?, config?): Promise<AxiosResponse>
 * - delete(url, config?): Promise<AxiosResponse>
 * - head(url, config?): Promise<AxiosResponse>
 * - options(url, config?): Promise<AxiosResponse>
 * - request(config): Promise<AxiosResponse>
 *
 * Plus custom method:
 * - extend(config): Create a new HttpClient instance with merged config
 *
 * Response structure (AxiosResponse):
 * - data: The response body (automatically parsed if JSON)
 * - status: HTTP status code (e.g., 200, 404, 500)
 * - statusText: HTTP status text (e.g., 'OK', 'Not Found')
 * - headers: Response headers object
 * - config: The request configuration
 * - request: The request object
 *
 * Error structure:
 * - Standard errors: Follow AxiosError structure with response, config, code, etc.
 * - Netlify timeouts: Transformed with isNetlifyTimeout=true and code='NETLIFY_TIMEOUT'
 * - Network errors: No response property, just message and code
 */

/**
 * @callback CalculateDelayFunction
 * @param {number} attemptCount - The current retry attempt number (1 for first retry, 2 for second, etc.)
 * @returns {number} The delay in milliseconds before the next retry (return 0 to stop retrying)
 */

/**
 * Creates an HTTP client with retry logic and Netlify timeout handling
 * Only handles internal requests (relative URLs). External requests (CORS) have to be done
 * through Netlify Functions.
 *
 * @param {object} [options] - Optional configuration overrides
 * @param {number} [options.timeout] - Request timeout in milliseconds (default: HTTP_REQUEST_TIMEOUT_MS)
 * @param {object} [options.retry] - Retry configuration
 * @param {number} [options.retry.limit] - Maximum number of retries (default: HTTP_RETRY_LIMIT)
 * @param {CalculateDelayFunction} [options.retry.calculateDelay] - Custom delay calculation function
 * @param {Record<string, string>} [options.headers] - Additional headers to include in requests
 * @returns {HttpClient} HTTP client with axios methods plus extend()
 */
const createHttpClient = (options = {}) => {
    // Create client for internal requests
    const client = axios.create({
        baseURL: getBaseUrl(),
        timeout: options.timeout || HTTP_REQUEST_TIMEOUT_MS,
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...options.headers,
        },
    });

    // Configure retry logic
    axiosRetry(client, {
        retries: options.retry?.limit || HTTP_RETRY_LIMIT,
        retryDelay: (retryCount) => {
            if (options.retry?.calculateDelay) {
                return options.retry.calculateDelay(retryCount);
            }
            // Use exponential backoff with defined delays
            return HTTP_RETRY_DELAYS_MS[retryCount - 1] || HTTP_RETRY_MAX_DELAY_MS;
        },
        retryCondition: (error) => {
            // List of status codes that should trigger a retry.
            // Each code is included for the following reasons:
            // 408: Request Timeout - transient, retrying may succeed
            // 429: Too Many Requests - retries with exponential backoff
            //      TODO: Implement proper rate limit handling by respecting Retry-After header
            // 500: Internal Server Error - server error, may be transient
            // 502: Bad Gateway - server/proxy error, often transient
            // 503: Service Unavailable - server overloaded or down for maintenance, may be transient
            // 504: Gateway Timeout - server/proxy timeout, may be transient
            const retryStatusCodes = [408, 429, 500, 502, 503, 504];

            if (error.response) {
                return retryStatusCodes.includes(error.response.status);
            }

            // Retry on network errors
            // ETIMEDOUT: Connection timed out
            // ECONNRESET: Connection reset by peer
            // ECONNREFUSED: Connection refused
            // ENOTFOUND: DNS lookup failed
            // ENETUNREACH: Network unreachable
            const retryErrorCodes = [
                'ETIMEDOUT',
                'ECONNRESET',
                'ECONNREFUSED',
                'ENOTFOUND',
                'ENETUNREACH',
            ];
            return error.code && retryErrorCodes.includes(error.code);
        },
        onRetry: (retryCount, error) => {
            addBreadcrumb({
                message: `HTTP retry attempt ${retryCount}`,
                category: 'http.retry',
                level: 'info',
                data: {
                    url: error.config?.url,
                    method: error.config?.method,
                    status_code: error.response?.status,
                    error_code: error.code,
                    error_message: error.message,
                    retry_count: retryCount,
                },
            });
        },
    });

    // Add response interceptor middleware for timeout detection
    client.interceptors.response.use((response) => {
        // Just return the response as-is (axios format)
        return response;
    }, handleBaseErrors);

    // Add extend method for creating new instances with merged options
    client.extend = (config) => createHttpClient({ ...options, ...config });

    return client;
};

// Default instance
/** @type {HttpClient} */
const httpClient = createHttpClient();

// Exports
export default httpClient;
export { createHttpClient };
